import { composeHexPathAtlas } from './pathAutotiler/hexPathProcedural';
import { HEX_PATH_BRUSH_COUNT } from './pathAutotiler/hexPathBrushCatalog';
import { composeSquarePathBrushAtlas, composeSquarePathQuarterAtlas } from './pathAutotiler/squarePathProcedural';
import { composeTrianglePathAtlas, composeTrianglePathPreviewAtlas } from './pathAutotiler/trianglePathProcedural';
import {
  PATH_TILE_ART_STYLES,
  getPathTileArtStyle,
  getPathTileTexturePath,
  type PathTileAtlasId,
  type PathTileTextureGridType,
} from './pathArtStyles';
export type PathTileGridShape = 'square' | 'hex' | 'triangle';
export type PathTileAutotileProfileId = 'square-path-v1' | 'hex-path-v1' | 'triangle-path-v1';
export type PathTileAtlasSource = 'image' | 'procedural';

interface PathTileAtlasMetadata {
  readonly id: PathTileAtlasId;
  readonly label: string;
  readonly source: PathTileAtlasSource;
  readonly imagePath?: string;
  readonly tileSize: number;
  readonly columns: number;
  readonly rows: number;
  readonly drawScale?: number;
  readonly autotileProfile: PathTileAutotileProfileId;
}

export interface PathTileAtlasDefinition extends PathTileAtlasMetadata { }

export interface PathTileLayerDefinition {
  readonly atlasId: PathTileAtlasId;
  readonly layerId: string;
  readonly label: string;
}

export interface PathTileQuarterAtlasDefinition extends PathTileAtlasMetadata { }

interface PathTileAtlasVariantDefinition {
  readonly preview: PathTileAtlasDefinition;
  readonly render: PathTileQuarterAtlasDefinition;
}

const DEFAULT_GRID_SHAPE: PathTileGridShape = 'square';

const PATH_TILE_ATLAS_VARIANTS: Readonly<Record<PathTileAtlasId, Readonly<Record<PathTileGridShape, PathTileAtlasVariantDefinition>>>> = {
  dirt: {
    square: {
      preview: {
        id: 'dirt',
        label: 'Dirt Paths',
        source: 'image',
        imagePath: '/visual-assets/paths/dirt_paths_preview.png',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'square-path-v1',
      },
      render: {
        id: 'dirt',
        label: 'Dirt Paths',
        source: 'image',
        imagePath: '/visual-assets/paths/dirt_paths_2.png',
        tileSize: 64,
        columns: 5,
        rows: 1,
        autotileProfile: 'square-path-v1',
      },
    },
    hex: {
      preview: {
        id: 'dirt',
        label: 'Dirt Paths',
        source: 'procedural',
        tileSize: 64,
        columns: 8, rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
      render: {
        id: 'dirt',
        label: 'Dirt Paths',
        source: 'procedural',
        tileSize: 128,
        columns: 8, rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
    },
    triangle: {
      preview: {
        id: 'dirt',
        label: 'Dirt Paths',
        source: 'procedural',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
      render: {
        id: 'dirt',
        label: 'Dirt Paths',
        source: 'procedural',
        tileSize: 64,
        columns: 8,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
    },
  },
  water: {
    square: {
      preview: {
        id: 'water',
        label: 'Water',
        source: 'image',
        imagePath: '/visual-assets/paths/water_paths_preview.png',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'square-path-v1',
      },
      render: {
        id: 'water',
        label: 'Water',
        source: 'image',
        imagePath: '/visual-assets/paths/water_paths_2.png',
        tileSize: 64,
        columns: 5,
        rows: 1,
        autotileProfile: 'square-path-v1',
      },
    },
    hex: {
      preview: {
        id: 'water',
        label: 'Water',
        source: 'procedural',
        tileSize: 64,
        columns: 8, rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
      render: {
        id: 'water',
        label: 'Water',
        source: 'procedural',
        tileSize: 128,
        columns: 8, rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
    },
    triangle: {
      preview: {
        id: 'water',
        label: 'Water',
        source: 'procedural',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
      render: {
        id: 'water',
        label: 'Water',
        source: 'procedural',
        tileSize: 64,
        columns: 8,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
    },
  },
  flowers: {
    square: {
      preview: {
        id: 'flowers',
        label: 'Flower Patch',
        source: 'procedural',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'square-path-v1',
      },
      render: {
        id: 'flowers',
        label: 'Flower Patch',
        source: 'procedural',
        tileSize: 64,
        columns: 5,
        rows: 1,
        autotileProfile: 'square-path-v1',
      },
    },
    hex: {
      preview: {
        id: 'flowers',
        label: 'Flower Patch',
        source: 'procedural',
        tileSize: 64,
        columns: 8,
        rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
      render: {
        id: 'flowers',
        label: 'Flower Patch',
        source: 'procedural',
        tileSize: 128,
        columns: 8,
        rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
    },
    triangle: {
      preview: {
        id: 'flowers',
        label: 'Flower Patch',
        source: 'procedural',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
      render: {
        id: 'flowers',
        label: 'Flower Patch',
        source: 'procedural',
        tileSize: 64,
        columns: 8,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
    },
  },
  brick_road: {
    square: {
      preview: {
        id: 'brick_road',
        label: 'Brick Road',
        source: 'procedural',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'square-path-v1',
      },
      render: {
        id: 'brick_road',
        label: 'Brick Road',
        source: 'procedural',
        tileSize: 64,
        columns: 5,
        rows: 1,
        autotileProfile: 'square-path-v1',
      },
    },
    hex: {
      preview: {
        id: 'brick_road',
        label: 'Brick Road',
        source: 'procedural',
        tileSize: 64,
        columns: 8,
        rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
      render: {
        id: 'brick_road',
        label: 'Brick Road',
        source: 'procedural',
        tileSize: 128,
        columns: 8,
        rows: 8,
        drawScale: 1.9,
        autotileProfile: 'hex-path-v1',
      },
    },
    triangle: {
      preview: {
        id: 'brick_road',
        label: 'Brick Road',
        source: 'procedural',
        tileSize: 64,
        columns: 4,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
      render: {
        id: 'brick_road',
        label: 'Brick Road',
        source: 'procedural',
        tileSize: 64,
        columns: 8,
        rows: 4,
        autotileProfile: 'triangle-path-v1',
      },
    },
  },
} as const;

export const PATH_TILE_ATLASES: readonly PathTileAtlasDefinition[] = Object.freeze(
  Object.values(PATH_TILE_ATLAS_VARIANTS).map((variants) => variants.square.preview),
);

export const PATH_TILE_QUARTER_ATLASES: readonly PathTileQuarterAtlasDefinition[] = Object.freeze(
  Object.values(PATH_TILE_ATLAS_VARIANTS).map((variants) => variants.square.render),
);

export const PATH_TILE_LAYERS: readonly PathTileLayerDefinition[] = Object.freeze(
  PATH_TILE_ART_STYLES.map((style) => ({
    atlasId: style.atlasId,
    layerId: style.layerId,
    label: style.label,
  }))
);

const layerByAtlasId = new Map(PATH_TILE_LAYERS.map((layer) => [layer.atlasId, layer] as const));
const previewAtlasImageCache = new Map<string, Promise<HTMLImageElement>>();
const quarterAtlasImageCache = new Map<string, Promise<HTMLImageElement>>();
const layerAliasesByAtlasId: Record<PathTileAtlasId, readonly string[]> = Object.fromEntries(
  PATH_TILE_ART_STYLES.map((style) => [style.atlasId, style.aliases])
) as Record<PathTileAtlasId, readonly string[]>;

const loadedTextures = new Map<string, HTMLImageElement>();
const loadingTextures = new Map<string, Promise<HTMLImageElement>>();

export function getPathTileTextureSync(
  atlasId: PathTileAtlasId,
  gridType: PathTileTextureGridType = 'square',
): HTMLImageElement | undefined {
  const texturePath = getPathTileTexturePath(atlasId, gridType);
  return texturePath ? loadedTextures.get(texturePath) : undefined;
}

export async function preloadPathTileTexture(
  atlasId: PathTileAtlasId,
  gridType: PathTileTextureGridType = 'square',
): Promise<HTMLImageElement | undefined> {
  const texturePath = getPathTileTexturePath(atlasId, gridType);
  if (!texturePath) return undefined;

  if (loadedTextures.has(texturePath)) {
    return loadedTextures.get(texturePath)!;
  }
  if (loadingTextures.has(texturePath)) {
    return loadingTextures.get(texturePath)!;
  }

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      loadedTextures.set(texturePath, img);
      resolve(img);
    };
    img.onerror = () => reject(new Error(`Failed to load path texture ${texturePath}`));
    img.src = texturePath;
  });
  loadingTextures.set(texturePath, promise);
  return promise;
}

function normalizeGridShape(gridType?: string | null): PathTileGridShape {
  if (gridType === 'hex' || gridType === 'triangle') {
    return gridType;
  }
  return DEFAULT_GRID_SHAPE;
}

function getAtlasVariant(atlasId: PathTileAtlasId, gridType?: string | null): PathTileAtlasVariantDefinition | null {
  const variants = PATH_TILE_ATLAS_VARIANTS[atlasId];
  return variants?.[normalizeGridShape(gridType)] ?? null;
}

function imageCacheKey(atlasId: PathTileAtlasId, gridType?: string | null): string {
  return `${normalizeGridShape(gridType)}:${atlasId}`;
}

function resolveTextureTileScale(atlasId: PathTileAtlasId, atlas: PathTileAtlasDefinition | PathTileQuarterAtlasDefinition): number | undefined {
  const baseScale = getPathTileArtStyle(atlasId).textureTileScale;
  if (!baseScale) {
    return undefined;
  }
  return atlas.drawScale ? (baseScale / atlas.drawScale) : baseScale;
}

export function getPathTileAtlasDefinition(atlasId: PathTileAtlasId, gridType?: string | null): PathTileAtlasDefinition | null {
  return getAtlasVariant(atlasId, gridType)?.preview ?? null;
}

export function getPathTileQuarterAtlasDefinition(atlasId: PathTileAtlasId, gridType?: string | null): PathTileQuarterAtlasDefinition | null {
  return getAtlasVariant(atlasId, gridType)?.render ?? null;
}

export function getPathTileLayerDefinition(atlasId: PathTileAtlasId): PathTileLayerDefinition | null {
  return layerByAtlasId.get(atlasId) ?? null;
}

export function matchesPathTileLayerId(atlasId: PathTileAtlasId, layerId: string): boolean {
  return layerAliasesByAtlasId[atlasId].includes(layerId);
}

export function normalizePathTileLayerId(atlasId: PathTileAtlasId, layerId: string): string {
  return matchesPathTileLayerId(atlasId, layerId)
    ? getPathTileLayerDefinition(atlasId)?.layerId ?? layerId
    : layerId;
}

export function getPathTileBrushCount(atlasId: PathTileAtlasId, gridType?: string | null): number {
  const atlas = getPathTileAtlasDefinition(atlasId, gridType);
  if (!atlas) {
    return 0;
  }
  return normalizeGridShape(gridType) === 'hex'
    ? HEX_PATH_BRUSH_COUNT
    : atlas.columns * atlas.rows;
}

export async function loadPathTilePreviewAtlasImage(atlasId: PathTileAtlasId, gridType?: string | null): Promise<HTMLImageElement> {
  const cacheKey = imageCacheKey(atlasId, gridType);
  const existing = previewAtlasImageCache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const atlas = getPathTileAtlasDefinition(atlasId, gridType);
  if (!atlas) {
    throw new Error(`Unknown path preview atlas '${atlasId}'.`);
  }
  if (normalizeGridShape(gridType) === 'hex') {
    const texture = await preloadPathTileTexture(atlasId, 'hex');
    const pending = canvasToImage(
      composeHexPathAtlas(atlasId, atlas.tileSize, texture, resolveTextureTileScale(atlasId, atlas))
    );
    previewAtlasImageCache.set(cacheKey, pending);
    return pending;
  }
  if (normalizeGridShape(gridType) === 'triangle') {
    const pending = canvasToImage(composeTrianglePathPreviewAtlas(atlasId, atlas.tileSize));
    previewAtlasImageCache.set(cacheKey, pending);
    return pending;
  }
  const texture = await preloadPathTileTexture(atlasId, 'square');
  const pending = canvasToImage(composeSquarePathBrushAtlas(atlasId, atlas.tileSize, texture));
  previewAtlasImageCache.set(cacheKey, pending);
  return pending;
}

export async function loadPathTileAtlasImage(atlasId: PathTileAtlasId, gridType?: string | null): Promise<HTMLImageElement> {
  const cacheKey = imageCacheKey(atlasId, gridType);
  const existing = quarterAtlasImageCache.get(cacheKey);
  if (existing) {
    return existing;
  }
  const atlas = getPathTileQuarterAtlasDefinition(atlasId, gridType);
  if (!atlas) {
    throw new Error(`Unknown path tile atlas '${atlasId}'.`);
  }
  if (normalizeGridShape(gridType) === 'hex') {
    const texture = await preloadPathTileTexture(atlasId, 'hex');
    const pending = canvasToImage(
      composeHexPathAtlas(atlasId, atlas.tileSize, texture, resolveTextureTileScale(atlasId, atlas))
    );
    quarterAtlasImageCache.set(cacheKey, pending);
    return pending;
  }
  if (normalizeGridShape(gridType) === 'triangle') {
    const pending = canvasToImage(composeTrianglePathAtlas(atlasId, atlas.tileSize));
    quarterAtlasImageCache.set(cacheKey, pending);
    return pending;
  }
  const texture = await preloadPathTileTexture(atlasId, 'square');
  const pending = canvasToImage(composeSquarePathQuarterAtlas(atlasId, atlas.tileSize, texture));
  quarterAtlasImageCache.set(cacheKey, pending);
  return pending;
}

function canvasToImage(canvas: HTMLCanvasElement): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to create path atlas image.'));
    image.src = canvas.toDataURL();
  });
}

// Preload all textures
for (const style of PATH_TILE_ART_STYLES) {
  if (style.texturePath || style.texturePathByGrid) {
    void preloadPathTileTexture(style.atlasId, 'square').catch(console.error);
    void preloadPathTileTexture(style.atlasId, 'hex').catch(console.error);
    void preloadPathTileTexture(style.atlasId, 'triangle').catch(console.error);
  }
}
